import {
  type DropTargetGetFeedback,
  useDraggable,
  useDropTarget,
} from '@affine/component';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import {
  type ComponentProps,
  type DragEvent as ReactDragEvent,
  forwardRef,
  type ReactElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

import * as navigationTreeStyles from '../../desktop/components/navigation-panel/tree/node.css';
import { isSidebarAreaHeaderDropTarget } from '../../desktop/components/navigation-panel/tree/sidebar-area-drag-hover';
import { useSidebarAreaHeaderDropTarget } from '../root-app-sidebar/use-sidebar-area-header-drop-target';
import { SidebarDisclosureItem } from '../sidebar-disclosure-item';
import {
  SidebarExpandableGroup,
  SidebarExpandableSection,
} from '../sidebar-expandable-section';
import {
  SidebarNavigationFilterEmpty,
  useSidebarNavigationFilter,
} from '../sidebar-navigation-filter';
import * as styles from './index.css';

export type WorkspaceAppSidebarElementProps = Omit<
  ComponentProps<'div'>,
  'children' | 'className'
> & {
  [attribute: `data-${string}`]: string | number | boolean | undefined;
};

export function WorkspaceAppSidebarFrame({
  as: Root = 'aside',
  children,
  className,
  contentClassName,
  contentProps,
  footer,
  header,
  rootProps,
  status,
}: {
  as?: 'aside' | 'div';
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
  contentProps?: WorkspaceAppSidebarElementProps;
  footer?: ReactNode;
  header: ReactNode;
  rootProps?: WorkspaceAppSidebarElementProps;
  status?: ReactNode;
}): ReactElement {
  return (
    <Root {...rootProps} className={clsx(styles.surface, className)}>
      {header}
      {status}
      <div {...contentProps} className={clsx(styles.body, contentClassName)}>
        {children}
      </div>
      {footer}
    </Root>
  );
}

export interface WorkspaceAppScopedSidebarItem {
  scope: 'private' | 'shared';
}

export interface WorkspaceAppSidebarAreaNode {
  key: string;
  scope: 'private' | 'shared';
  title: ReactNode;
}

export interface WorkspaceAppSidebarSourceArea {
  kind: 'app' | 'private' | 'public' | 'teamspace';
  name: string;
  rootFolderId: string;
}

interface WorkspaceAppSidebarDndData extends AffineDNDData {
  draggable: AffineDNDData['draggable'] & {
    workspaceAppSidebarAreaItemKey: string;
  };
}

const noopWorkspaceAppSidebarHeaderDrop = () => {};

function WorkspaceAppSidebarDraggableItem<TItem>({
  item,
  itemKey,
  movable,
  onDragEnd,
  onDragStart,
  renderItem,
}: {
  item: TItem;
  itemKey: string;
  movable: boolean;
  onDragEnd?: () => void;
  onDragStart: (item: TItem) => void;
  renderItem: (
    item: TItem,
    dragHandleRef?: RefObject<HTMLDivElement>
  ) => ReactNode;
}): ReactElement {
  const { dragHandleRef, dragRef, dragging, draggingPosition, dropTarget } =
    useDraggable<WorkspaceAppSidebarDndData>(
      () => ({
        canDrag: movable,
        data: { workspaceAppSidebarAreaItemKey: itemKey },
        disableDragPreview: true,
        onDragStart: () => onDragStart(item),
        onDrop: onDragEnd,
      }),
      [item, itemKey, movable, onDragEnd, onDragStart]
    );
  const areaHeaderHoverActive = dropTarget.some(target =>
    isSidebarAreaHeaderDropTarget(target.data)
  );

  return (
    <>
      <div className={styles.areaItem} ref={dragRef}>
        {renderItem(item, dragHandleRef)}
      </div>
      {dragging && !draggingPosition.outWindow
        ? createPortal(
            <div
              className={navigationTreeStyles.liveDraggingPortal}
              data-area-hover-expand={areaHeaderHoverActive}
              data-testid="workspace-app-sidebar-live-drag-preview"
              style={{
                transform: `translate3d(${draggingPosition.clientX + 8}px, ${draggingPosition.clientY + 4}px, 0)`,
              }}
            >
              <div className={navigationTreeStyles.draggingContainer}>
                <div className={styles.areaDragPreview}>{renderItem(item)}</div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export function createWorkspaceAppSidebarAreaNodes<
  TArea extends WorkspaceAppSidebarSourceArea,
>(
  areas: readonly TArea[],
  publicAreaTitle: ReactNode
): Array<WorkspaceAppSidebarAreaNode & { area: TArea }> {
  return areas
    .filter(area => area.kind !== 'app')
    .map(area => ({
      area,
      key: area.rootFolderId,
      scope:
        area.kind === 'private' ? ('private' as const) : ('shared' as const),
      title: area.kind === 'public' ? publicAreaTitle : area.name,
    }));
}

function WorkspaceAppSidebarNestedArea<
  TArea extends WorkspaceAppSidebarAreaNode,
>({
  area,
  canDropDraggedItem,
  children,
  emptyState,
  itemCount,
  onDropDraggedItem,
  persistenceKey,
  canRevealDraggedItem,
}: {
  area: TArea;
  canDropDraggedItem: (
    source: WorkspaceAppSidebarDndData['draggable']
  ) => boolean;
  children: ReactNode;
  emptyState?: ReactNode;
  itemCount: number;
  onDropDraggedItem: (source: WorkspaceAppSidebarDndData['draggable']) => void;
  persistenceKey: string;
  canRevealDraggedItem: (
    source: WorkspaceAppSidebarDndData['draggable']
  ) => boolean;
}): ReactElement {
  const navigationPanelService = useService(NavigationPanelService);
  const filter = useSidebarNavigationFilter();
  const collapsedPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'area', area.key],
    [area.key, persistenceKey]
  );
  const storedCollapsed = useLiveData(
    navigationPanelService.collapsed$(collapsedPath)
  );
  const collapsed = !filter.active && storedCollapsed;
  const canDropOnCollapsedHeader = useCallback(
    (args: DropTargetGetFeedback<WorkspaceAppSidebarDndData>) =>
      collapsed && canRevealDraggedItem(args.source.data),
    [canRevealDraggedItem, collapsed]
  );
  const { dropTargetRef: headerDropTargetRef } =
    useSidebarAreaHeaderDropTarget<WorkspaceAppSidebarDndData>({
      canDrop: canDropOnCollapsedHeader,
      collapsed,
      onDrop: noopWorkspaceAppSidebarHeaderDrop,
      path: collapsedPath,
    });
  const { draggedOver, dropTargetRef } =
    useDropTarget<WorkspaceAppSidebarDndData>(
      () => ({
        canDrop: args => canDropDraggedItem(args.source.data),
        dropEffect: 'move',
        onDrop: args => onDropDraggedItem(args.source.data),
      }),
      [canDropDraggedItem, onDropDraggedItem]
    );

  return (
    <div
      className={styles.nestedAreaDropTarget}
      data-drag-over={draggedOver ? true : undefined}
      data-workspace-app-sidebar-area-key={area.key}
      ref={dropTargetRef}
    >
      <SidebarDisclosureItem
        headerRef={headerDropTargetRef}
        size="subsection"
        expanded={!collapsed}
        animate={filter.animate}
        animateContent={false}
        onExpandedChange={expanded => {
          if (!filter.active)
            navigationPanelService.setCollapsed(collapsedPath, !expanded);
        }}
        data-testid="workspace-app-sidebar-area"
        title={area.title}
      >
        <div className={styles.nestedAreaItems}>
          {itemCount === 0 && emptyState !== undefined ? (
            <div
              className={styles.nestedAreaEmptyState}
              data-sidebar-section-snap-target="true"
            >
              {emptyState}
            </div>
          ) : (
            children
          )}
        </div>
      </SidebarDisclosureItem>
    </div>
  );
}

/**
 * Shared exact-area tree for workspace apps. The tree owns common grouping,
 * persistence, expand/collapse and drag chrome; each app only renders its
 * resource row and handles the requested area transition.
 */
export function WorkspaceAppSidebarAreaTree<
  TItem,
  TArea extends WorkspaceAppSidebarAreaNode,
>({
  areas: allAreas,
  canDropItem,
  canMoveItem,
  emptyState,
  getItemAreaKey,
  getItemKey,
  getItemSearchText,
  items: allItems,
  onItemAreaChangeRequest,
  onItemDragStart,
  personalTitle,
  persistenceKey,
  renderItem,
  renderSectionAction,
  teamAreasTitle,
}: {
  areas: readonly TArea[];
  canDropItem?: (item: TItem, targetArea: TArea) => boolean;
  canMoveItem?: (item: TItem) => boolean;
  emptyState?: (scope: WorkspaceAppSidebarAreaNode['scope']) => ReactNode;
  getItemAreaKey: (item: TItem) => string;
  getItemKey: (item: TItem) => string;
  getItemSearchText?: (item: TItem) => string;
  items: readonly TItem[];
  onItemAreaChangeRequest?: (item: TItem, targetArea: TArea) => void;
  onItemDragStart?: (item: TItem) => void;
  personalTitle: ReactNode;
  persistenceKey: string;
  renderItem: (
    item: TItem,
    dragHandleRef?: RefObject<HTMLDivElement>
  ) => ReactNode;
  renderSectionAction?: (
    scope: WorkspaceAppSidebarAreaNode['scope']
  ) => ReactNode;
  teamAreasTitle: ReactNode;
}): ReactElement {
  const navigationPanelService = useService(NavigationPanelService);
  const filter = useSidebarNavigationFilter();
  const itemMatches = (item: TItem) => {
    const area = allAreas.find(area => area.key === getItemAreaKey(item));
    return (
      filter.matches(getItemSearchText?.(item) ?? getItemKey(item)) ||
      filter.matches(String(area?.title ?? '')) ||
      filter.matches(
        String(area?.scope === 'private' ? personalTitle : teamAreasTitle)
      )
    );
  };
  const items = filter.active ? allItems.filter(itemMatches) : allItems;
  const areas = filter.active
    ? allAreas.filter(
        area =>
          filter.matches(String(area.title)) ||
          filter.matches(
            String(area.scope === 'private' ? personalTitle : teamAreasTitle)
          ) ||
          items.some(item => getItemAreaKey(item) === area.key)
      )
    : allAreas;
  const personalPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'personal'],
    [persistenceKey]
  );
  const teamAreasPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'teamAreas'],
    [persistenceKey]
  );
  const storedPersonalCollapsed = useLiveData(
    navigationPanelService.collapsed$(personalPath)
  );
  const storedTeamAreasCollapsed = useLiveData(
    navigationPanelService.collapsed$(teamAreasPath)
  );
  const personalCollapsed = !filter.active && storedPersonalCollapsed;
  const teamAreasCollapsed = !filter.active && storedTeamAreasCollapsed;
  const personalExpanded = !personalCollapsed;
  const teamAreasExpanded = !teamAreasCollapsed;
  const privateAreas = useMemo(
    () => areas.filter(area => area.scope === 'private'),
    [areas]
  );
  const sharedAreas = useMemo(
    () => areas.filter(area => area.scope === 'shared'),
    [areas]
  );
  const itemsByArea = useMemo(() => {
    const grouped = new Map<string, TItem[]>();
    for (const area of areas) grouped.set(area.key, []);
    for (const item of items) {
      const areaKey = getItemAreaKey(item);
      const areaItems = grouped.get(areaKey);
      if (areaItems) areaItems.push(item);
    }
    return grouped;
  }, [areas, getItemAreaKey, items]);
  const itemsByKey = useMemo(
    () => new Map(items.map(item => [getItemKey(item), item] as const)),
    [getItemKey, items]
  );
  const getDraggedItem = useCallback(
    (source: WorkspaceAppSidebarDndData['draggable']) =>
      itemsByKey.get(source.workspaceAppSidebarAreaItemKey),
    [itemsByKey]
  );
  const personalItems = useMemo(
    () => privateAreas.flatMap(area => itemsByArea.get(area.key) ?? []),
    [itemsByArea, privateAreas]
  );
  const sharedItemCount = useMemo(
    () =>
      sharedAreas.reduce(
        (count, area) => count + (itemsByArea.get(area.key)?.length ?? 0),
        0
      ),
    [itemsByArea, sharedAreas]
  );
  const renderDraggableItem = useCallback(
    (item: TItem) => {
      const movable =
        onItemAreaChangeRequest !== undefined && (canMoveItem?.(item) ?? true);
      return (
        <WorkspaceAppSidebarDraggableItem
          item={item}
          itemKey={getItemKey(item)}
          key={getItemKey(item)}
          movable={movable}
          onDragStart={draggedItem => {
            onItemDragStart?.(draggedItem);
          }}
          renderItem={renderItem}
        />
      );
    },
    [
      canMoveItem,
      getItemKey,
      onItemAreaChangeRequest,
      onItemDragStart,
      renderItem,
    ]
  );
  const canDropOnArea = useCallback(
    (targetArea: TArea, source: WorkspaceAppSidebarDndData['draggable']) => {
      const item = getDraggedItem(source);
      return Boolean(
        item &&
        getItemAreaKey(item) !== targetArea.key &&
        canDropItem?.(item, targetArea) !== false
      );
    },
    [canDropItem, getDraggedItem, getItemAreaKey]
  );
  const dropOnArea = useCallback(
    (targetArea: TArea, source: WorkspaceAppSidebarDndData['draggable']) => {
      const item = getDraggedItem(source);
      if (
        !item ||
        getItemAreaKey(item) === targetArea.key ||
        canDropItem?.(item, targetArea) === false
      ) {
        return;
      }
      onItemAreaChangeRequest?.(item, targetArea);
    },
    [canDropItem, getDraggedItem, getItemAreaKey, onItemAreaChangeRequest]
  );
  const privateDropArea = privateAreas[0];
  const {
    draggedOver: personalAreaDraggedOver,
    dropTargetRef: personalDropRef,
  } = useDropTarget<WorkspaceAppSidebarDndData>(
    () => ({
      canDrop: args =>
        Boolean(
          privateDropArea && canDropOnArea(privateDropArea, args.source.data)
        ),
      dropEffect: 'move',
      onDrop: args => {
        if (privateDropArea) dropOnArea(privateDropArea, args.source.data);
      },
    }),
    [canDropOnArea, dropOnArea, privateDropArea]
  );
  const canRevealPersonalHeader = useCallback(
    (args: DropTargetGetFeedback<WorkspaceAppSidebarDndData>) => {
      const item = getDraggedItem(args.source.data);
      return Boolean(
        personalCollapsed &&
        item &&
        privateDropArea &&
        getItemAreaKey(item) !== privateDropArea.key &&
        canDropItem?.(item, privateDropArea) !== false
      );
    },
    [
      canDropItem,
      getDraggedItem,
      getItemAreaKey,
      personalCollapsed,
      privateDropArea,
    ]
  );
  const canRevealTeamAreasHeader = useCallback(
    (args: DropTargetGetFeedback<WorkspaceAppSidebarDndData>) => {
      const item = getDraggedItem(args.source.data);
      return Boolean(
        teamAreasCollapsed &&
        item &&
        sharedAreas.some(
          area =>
            getItemAreaKey(item) !== area.key &&
            canDropItem?.(item, area) !== false
        )
      );
    },
    [
      canDropItem,
      getDraggedItem,
      getItemAreaKey,
      sharedAreas,
      teamAreasCollapsed,
    ]
  );
  const { dropTargetRef: personalHeaderDropTargetRef } =
    useSidebarAreaHeaderDropTarget<WorkspaceAppSidebarDndData>({
      canDrop: canRevealPersonalHeader,
      collapsed: personalCollapsed,
      onDrop: noopWorkspaceAppSidebarHeaderDrop,
      path: personalPath,
    });
  const { dropTargetRef: teamAreasHeaderDropTargetRef } =
    useSidebarAreaHeaderDropTarget<WorkspaceAppSidebarDndData>({
      canDrop: canRevealTeamAreasHeader,
      collapsed: teamAreasCollapsed,
      onDrop: noopWorkspaceAppSidebarHeaderDrop,
      path: teamAreasPath,
    });

  return (
    <WorkspaceAppSidebarAreas
      persistenceKey={persistenceKey}
      personal={{
        hidden: filter.active && privateAreas.length === 0,
        emptyState: emptyState?.('private'),
        itemCount: personalItems.length,
        listProps: {
          'data-drag-over': personalAreaDraggedOver ? true : undefined,
        },
        listRef: personalDropRef,
        measurementRef: personalDropRef,
        groupProps: {
          expanded: personalExpanded,
          placement: 'top',
        },
        sectionProps: {
          action: renderSectionAction?.('private'),
          expanded: personalExpanded,
          headerRef: personalHeaderDropTargetRef,
          onExpandedChange: expanded =>
            !filter.active &&
            navigationPanelService.setCollapsed(personalPath, !expanded),
          title: personalTitle,
        },
        children: personalItems.map(renderDraggableItem),
      }}
      teamAreas={{
        hidden: filter.active && sharedAreas.length === 0,
        content:
          sharedAreas.length === 0 ? (
            <WorkspaceAppSidebarAreaList>
              <div
                className={styles.areaEmptyState}
                data-sidebar-section-snap-target="true"
              >
                {emptyState?.('shared')}
              </div>
            </WorkspaceAppSidebarAreaList>
          ) : (
            <WorkspaceAppSidebarAreaList className={styles.areaTreeList}>
              {sharedAreas.map(area => {
                const areaItems = itemsByArea.get(area.key) ?? [];
                return (
                  <WorkspaceAppSidebarNestedArea
                    area={area}
                    canDropDraggedItem={source => canDropOnArea(area, source)}
                    canRevealDraggedItem={source => {
                      const item = getDraggedItem(source);
                      return Boolean(
                        item &&
                        getItemAreaKey(item) !== area.key &&
                        canDropItem?.(item, area) !== false
                      );
                    }}
                    emptyState={emptyState?.('shared')}
                    itemCount={areaItems.length}
                    key={area.key}
                    onDropDraggedItem={source => dropOnArea(area, source)}
                    persistenceKey={persistenceKey}
                  >
                    {areaItems.map(renderDraggableItem)}
                  </WorkspaceAppSidebarNestedArea>
                );
              })}
            </WorkspaceAppSidebarAreaList>
          ),
        itemCount: sharedItemCount,
        groupProps: {
          expanded: teamAreasExpanded,
          fillLayout: 'single-top-fill',
          placement: 'top',
        },
        sectionProps: {
          action: renderSectionAction?.('shared'),
          expanded: teamAreasExpanded,
          headerRef: teamAreasHeaderDropTargetRef,
          onExpandedChange: expanded =>
            !filter.active &&
            navigationPanelService.setCollapsed(teamAreasPath, !expanded),
          title: teamAreasTitle,
        },
      }}
    />
  );
}

export function WorkspaceAppScopedSidebarAreas<
  TItem extends WorkspaceAppScopedSidebarItem,
>({
  items: allItems,
  emptyState,
  getItemKey,
  getItemSearchText,
  onItemScopeChangeRequest,
  personalTitle,
  persistenceKey,
  renderItem,
  renderSectionAction,
  teamAreasTitle,
}: {
  items: readonly TItem[];
  emptyState?: (scope: TItem['scope']) => ReactNode;
  getItemKey?: (item: TItem) => string;
  getItemSearchText?: (item: TItem) => string;
  onItemScopeChangeRequest?: (item: TItem, targetScope: TItem['scope']) => void;
  personalTitle: ReactNode;
  persistenceKey: string;
  renderItem: (item: TItem) => ReactNode;
  renderSectionAction?: (scope: TItem['scope']) => ReactNode;
  teamAreasTitle: ReactNode;
}): ReactElement {
  const navigationPanelService = useService(NavigationPanelService);
  const filter = useSidebarNavigationFilter();
  const items = filter.active
    ? allItems.filter(
        item =>
          filter.matches(
            getItemSearchText?.(item) ?? getItemKey?.(item) ?? ''
          ) ||
          filter.matches(
            String(item.scope === 'private' ? personalTitle : teamAreasTitle)
          )
      )
    : allItems;
  const draggedItemRef = useRef<TItem | null>(null);
  const personalPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'personal'],
    [persistenceKey]
  );
  const teamAreasPath = useMemo(
    () => ['workspaceApp', persistenceKey, 'teamAreas'],
    [persistenceKey]
  );
  const personalCollapsed = useLiveData(
    navigationPanelService.collapsed$(personalPath)
  );
  const teamAreasCollapsed = useLiveData(
    navigationPanelService.collapsed$(teamAreasPath)
  );
  const personalExpanded = filter.active || !personalCollapsed;
  const teamAreasExpanded = filter.active || !teamAreasCollapsed;
  const [personalItems, teamAreaItems] = useMemo(
    () => [
      items.filter(item => item.scope === 'private'),
      items.filter(item => item.scope === 'shared'),
    ],
    [items]
  );
  const renderScopedItem = useCallback(
    (item: TItem) => {
      const content = renderItem(item);
      if (!onItemScopeChangeRequest || !getItemKey) return content;
      return (
        <div
          className={styles.areaItem}
          key={getItemKey(item)}
          draggable
          onDragEnd={() => {
            draggedItemRef.current = null;
          }}
          onDragStart={event => {
            const nestedDraggable =
              event.target instanceof Element
                ? event.target.closest('[draggable="true"]')
                : null;
            if (nestedDraggable !== event.currentTarget) return;
            draggedItemRef.current = item;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData(
              'application/x-streamlain-scoped-sidebar-item',
              getItemKey(item)
            );
          }}
        >
          {content}
        </div>
      );
    },
    [getItemKey, onItemScopeChangeRequest, renderItem]
  );
  const listDropProps = useCallback(
    (targetScope: TItem['scope']): ComponentProps<'div'> => ({
      onDragOver: (event: ReactDragEvent<HTMLDivElement>) => {
        const item = draggedItemRef.current;
        if (!item || item.scope === targetScope) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      },
      onDrop: (event: ReactDragEvent<HTMLDivElement>) => {
        const item = draggedItemRef.current;
        if (!item || item.scope === targetScope) return;
        event.preventDefault();
        draggedItemRef.current = null;
        onItemScopeChangeRequest?.(item, targetScope);
      },
    }),
    [onItemScopeChangeRequest]
  );

  return (
    <WorkspaceAppSidebarAreas
      persistenceKey={persistenceKey}
      personal={{
        hidden: filter.active && personalItems.length === 0,
        emptyState: emptyState?.('private'),
        itemCount: personalItems.length,
        listProps: listDropProps('private'),
        groupProps: {
          expanded: personalExpanded,
          placement: 'top',
        },
        sectionProps: {
          action: renderSectionAction?.('private'),
          expanded: personalExpanded,
          onExpandedChange: expanded =>
            !filter.active &&
            navigationPanelService.setCollapsed(personalPath, !expanded),
          title: personalTitle,
        },
        children: personalItems.map(renderScopedItem),
      }}
      teamAreas={{
        hidden: filter.active && teamAreaItems.length === 0,
        emptyState: emptyState?.('shared'),
        itemCount: teamAreaItems.length,
        listProps: listDropProps('shared'),
        groupProps: {
          expanded: teamAreasExpanded,
          fillLayout: 'single-top-fill',
          placement: 'top',
        },
        sectionProps: {
          action: renderSectionAction?.('shared'),
          expanded: teamAreasExpanded,
          onExpandedChange: expanded =>
            !filter.active &&
            navigationPanelService.setCollapsed(teamAreasPath, !expanded),
          title: teamAreasTitle,
        },
        children: teamAreaItems.map(renderScopedItem),
      }}
    />
  );
}

type ExpandableGroupProps = Omit<
  ComponentProps<typeof SidebarExpandableGroup>,
  'children'
>;
type ExpandableSectionProps = Omit<
  ComponentProps<typeof SidebarExpandableSection>,
  'children'
>;

type WorkspaceAppSidebarAreaResizeProps = Pick<
  ExpandableSectionProps,
  | 'contentHeight'
  | 'onContentHeightChange'
  | 'onContentHeightCommit'
  | 'onResizeActiveChange'
>;

export type WorkspaceAppSidebarAreaResize =
  | { mode?: 'measured' }
  | ({ mode: 'controlled' } & WorkspaceAppSidebarAreaResizeProps);

type WorkspaceAppSidebarAreaListProps = Omit<
  ComponentProps<'div'>,
  'children' | 'ref'
> & {
  'data-drag-over'?: boolean;
};

export interface WorkspaceAppSidebarArea {
  hidden?: boolean;
  animate?: boolean;
  children?: ReactNode;
  content?: ReactNode;
  defaultVisibleItems?: number;
  emptyState?: ReactNode;
  groupProps: ExpandableGroupProps;
  itemCount?: number;
  layout?: 'default' | 'inherit';
  listProps?: WorkspaceAppSidebarAreaListProps;
  listRef?: ComponentProps<'div'>['ref'];
  measurementRef?: RefObject<HTMLDivElement | null>;
  minimumVisibleItems?: number;
  resize?: WorkspaceAppSidebarAreaResize;
  snapTargetSelector?: string;
  sectionProps: ExpandableSectionProps;
}

export function WorkspaceAppSidebarArea({
  children,
  content,
  emptyState,
  groupProps,
  itemCount,
  listProps,
  listRef,
  sectionProps,
  hidden = false,
  animate = true,
}: WorkspaceAppSidebarArea): ReactElement | null {
  if (hidden) return null;
  return (
    <div
      className={styles.navigationArea}
      data-expanded={sectionProps.expanded}
      data-placement={groupProps.placement}
    >
      <SidebarDisclosureItem
        title={sectionProps.title}
        actions={sectionProps.action}
        expanded={sectionProps.expanded}
        onExpandedChange={sectionProps.onExpandedChange}
        headerRef={sectionProps.headerRef}
        animate={animate}
        animateContent={false}
      >
        {content !== undefined ? (
          content
        ) : (
          <WorkspaceAppSidebarAreaList ref={listRef} {...listProps}>
            {itemCount === 0 && emptyState !== undefined ? (
              <div className={styles.areaEmptyState}>{emptyState}</div>
            ) : (
              children
            )}
          </WorkspaceAppSidebarAreaList>
        )}
      </SidebarDisclosureItem>
    </div>
  );
}

export function WorkspaceAppSidebarAreas({
  personal,
  persistenceKey,
  teamAreas,
}: {
  personal: WorkspaceAppSidebarArea;
  persistenceKey: string;
  teamAreas: WorkspaceAppSidebarArea;
}): ReactElement {
  const filter = useSidebarNavigationFilter();
  return (
    <div
      className={styles.navigationAreas}
      data-workspace-app-sidebar={persistenceKey}
    >
      <WorkspaceAppSidebarArea {...personal} animate={filter.animate} />
      <WorkspaceAppSidebarArea {...teamAreas} animate={filter.animate} />
      {filter.active && personal.hidden && teamAreas.hidden ? (
        <SidebarNavigationFilterEmpty />
      ) : null}
    </div>
  );
}

export const WorkspaceAppSidebarAreaList = forwardRef<
  HTMLDivElement,
  ComponentProps<'div'>
>(({ children, className, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    className={clsx(styles.areaList, className)}
    data-workspace-app-sidebar-area-list="true"
  >
    {children}
  </div>
));

WorkspaceAppSidebarAreaList.displayName = 'WorkspaceAppSidebarAreaList';
